import {
	CompanyUploadPreview,
	companyUploadPreviewSchema,
	CompanyUploadRow,
	CsvEnrichmentStatistics,
	csvEnrichmentStatisticsSchema,
	JobTitleScreeningPreview,
	jobTitleScreeningPreviewSchema,
} from "@signalist/shared-types";

import { api } from "@/redux/api";

import { RequestPaginationParams } from "@/types/request-params";

import {
	CsvEnrichment,
	CsvEnrichmentResponse,
	CsvEnrichmentResponseSchema,
	CsvEnrichmentSchema,
	SaveMappingResponse,
	SaveMappingResponseSchema,
	SuggestCsvMappingRequest,
	SuggestCsvMappingResponse,
	SuggestCsvMappingResponseSchema,
} from "../../pages/CsvEnrichment/schema/csvEnrichment";
import {
	EnrichAnywayRequest,
	EnrichAnywayResponse,
	EnrichAnywayResponseSchema,
	RetryNotFoundResponse,
	RetryNotFoundResponseSchema,
} from "../../pages/CsvEnrichment/schema/enrichAnyway";

export const csvEnrichmentApi = api.injectEndpoints({
	endpoints: (builder) => ({
		getCsvEnrichments: builder.query<CsvEnrichmentResponse, RequestPaginationParams>({
			query: (params) => ({
				url: "csvEnrichment",
				params: {
					...(params && { ...params }),
				},
				responseSchema: CsvEnrichmentResponseSchema,
			}),
			providesTags: ["CsvEnrichment"],
		}),
		getCsvEnrichment: builder.query<CsvEnrichment, number>({
			query: (id: number) => ({
				url: `csvEnrichment/${id}`,
				params: {
					id,
				},
				responseSchema: CsvEnrichmentSchema,
			}),
		}),
		getCsvEnrichmentContent: builder.query<string, number>({
			query: (id: number) => ({
				url: `csvEnrichment/content/${id}`,

				responseHandler: async (response: Response) => {
					if (!response.ok) {
						return { error: response.statusText, status: response.status };
					}

					return response.text();
				},
			}),
		}),
		getCsvEnrichmentForTable: builder.query<CsvEnrichment, number>({
			query: (id: number) => ({
				url: `csvEnrichment/${id}/table`,
				params: {
					id,
				},
				responseSchema: CsvEnrichmentSchema,
			}),
		}),
		downloadCsvEnrichment: builder.query<Blob, number>({
			query: (id: number) => ({
				url: `csvEnrichment/downloadSource/${id}`,
				params: {
					id,
				},

				responseHandler: async (response: Response) => {
					if (!response.ok) {
						return { error: response.statusText, status: response.status };
					}

					return response.blob();
				},
			}),
		}),
		triggerCsvEnrichment: builder.mutation<void, number>({
			query: (id) => ({
				url: `csvEnrichment/trigger/${id}`,
				method: "POST",
			}),
			invalidatesTags: ["CsvEnrichment"],
		}),
		updateCsvEnrichment: builder.mutation<void, Partial<CsvEnrichment>>({
			query: ({ id, ...body }) => ({
				url: `csvEnrichment/${id}`,
				method: "PATCH",
				body,
			}),
			invalidatesTags: ["CsvEnrichment"],
		}),
		createCsvEnrichment: builder.mutation<Partial<CsvEnrichment>, FormData>({
			query: (body) => ({
				url: `csvEnrichment/upload`,
				method: "POST",
				body,
				responseSchema: CsvEnrichmentSchema,
			}),
			invalidatesTags: (result, error, arg) => {
				if (arg.get("listId")) {
					return [{ type: "List", id: +(arg.get("listId") || 0) }, "CsvEnrichment"];
				}

				return ["CsvEnrichment"];
			},
		}),
		updateCsvEnrichmentFile: builder.mutation<
			Partial<CsvEnrichment>,
			{ form: FormData; id: number; listId: number }
		>({
			query: ({ form, id }) => ({
				url: `csvEnrichment/upload/${id}`,
				method: "POST",
				body: form,
				responseSchema: CsvEnrichmentSchema,
			}),
			invalidatesTags: (result, error, arg) => {
				if (arg.listId) {
					return [{ type: "List", id: +(arg.listId || 0) }, "CsvEnrichment"];
				}

				return ["CsvEnrichment"];
			},
		}),
		deleteCsvEnrichment: builder.mutation<Partial<CsvEnrichment>, { id: number; listId?: number }>({
			query: ({ id, listId }) => ({
				url: listId ? `csvEnrichment/${id}/list/${listId}` : `csvEnrichment/${id}`,
				method: "DELETE",
				responseSchema: CsvEnrichmentSchema,
			}),
			invalidatesTags: (result, error, arg) => {
				if (arg.listId) {
					return [{ type: "List", id: arg.listId }, "CsvEnrichment"];
				}

				return ["CsvEnrichment"];
			},
		}),

		/**
		 * Saves the field mapping of a delivered file (PREC-3091): activates it for the list's next
		 * run and counts how many of its rows the list already holds.
		 */
		saveCsvMapping: builder.mutation<
			SaveMappingResponse,
			{
				id: number;
				listId: number;
				configuration: { foundField: string; assignedField: string }[];
				screenTitles?: boolean;
			}
		>({
			query: ({ id, configuration, screenTitles }) => ({
				url: `csvEnrichment/${id}/mapping`,
				method: "PATCH",
				body: { configuration, ...(screenTitles === undefined ? {} : { screenTitles }) },
				responseSchema: SaveMappingResponseSchema,
			}),
			invalidatesTags: (result, error, arg) => [{ type: "List", id: arg.listId }, "CsvEnrichment"],
		}),
		suggestCsvMapping: builder.mutation<SuggestCsvMappingResponse, SuggestCsvMappingRequest>({
			query: (body) => ({
				url: `csvEnrichment/suggest-mapping`,
				method: "POST",
				body,
				responseSchema: SuggestCsvMappingResponseSchema,
			}),
		}),
		screenPreview: builder.mutation<JobTitleScreeningPreview, { titles: string[] }>({
			query: (body) => ({
				url: `csvEnrichment/screen-preview`,
				method: "POST",
				body,
				responseSchema: jobTitleScreeningPreviewSchema,
			}),
		}),
		getCsvEnrichmentStatistics: builder.query<CsvEnrichmentStatistics, { id: number }>({
			query: ({ id }) => ({
				url: `csvEnrichment/statistics/${id}`,
				responseSchema: csvEnrichmentStatisticsSchema,
			}),
			providesTags: ["CsvEnrichment"],
		}),
		companyPreview: builder.mutation<CompanyUploadPreview, { companies: CompanyUploadRow[] }>({
			query: (body) => ({
				url: `csvEnrichment/company-preview`,
				method: "POST",
				body,
				responseSchema: companyUploadPreviewSchema,
			}),
		}),
		enrichAnyway: builder.mutation<EnrichAnywayResponse, EnrichAnywayRequest>({
			query: ({ listId, ...body }) => ({
				url: `csvEnrichment/enrich-anyway/list/${listId}`,
				method: "POST",
				body,
				responseSchema: EnrichAnywayResponseSchema,
			}),
			invalidatesTags: (result, error, arg) => [
				{ type: "List", id: arg.listId },
				"ListPerson",
				"CsvEnrichment",
			],
		}),
		// PREC-4520: a lead's Retry names its lead; only that lead is searched again.
		retryNotFound: builder.mutation<
			RetryNotFoundResponse,
			{ listId: number; listPersonIds?: number[]; includeOthers?: boolean }
		>({
			query: ({ listId, ...body }) => ({
				url: `csvEnrichment/retry-not-found/list/${listId}`,
				method: "POST",
				body,
				responseSchema: RetryNotFoundResponseSchema,
			}),
			invalidatesTags: (result, error, { listId }) => [
				{ type: "List", id: listId },
				"ListPerson",
				"CsvEnrichment",
			],
		}),
		resetCsvEnrichment: builder.mutation<void, { id: number; listId: number }>({
			query: ({ id, listId }) => ({
				url: `csvEnrichment/reset/${id}/list/${listId}`,
				method: "POST",
			}),
			invalidatesTags: (result, error, arg) => {
				if (arg.listId) {
					return [{ type: "List", id: arg.listId }, "CsvEnrichment"];
				}

				return ["CsvEnrichment"];
			},
		}),
	}),
});

export const {
	useGetCsvEnrichmentsQuery,
	useLazyGetCsvEnrichmentsQuery,
	useGetCsvEnrichmentQuery,
	useDeleteCsvEnrichmentMutation,
	useTriggerCsvEnrichmentMutation,
	useCreateCsvEnrichmentMutation,
	useUpdateCsvEnrichmentMutation,
	useGetCsvEnrichmentForTableQuery,
	useLazyDownloadCsvEnrichmentQuery,
	useUpdateCsvEnrichmentFileMutation,
	useGetCsvEnrichmentContentQuery,
	useResetCsvEnrichmentMutation,
	useSuggestCsvMappingMutation,
	useSaveCsvMappingMutation,
	useScreenPreviewMutation,
	useCompanyPreviewMutation,
	useGetCsvEnrichmentStatisticsQuery,
	useEnrichAnywayMutation,
	useRetryNotFoundMutation,
} = csvEnrichmentApi;
export const accountApiReducer = csvEnrichmentApi.reducer;
export const accountApiMiddleware = csvEnrichmentApi.middleware;
